import type React from 'react';

import * as styles from './styles';
import { Button } from '@cobliteam/ui/components';
import moment from 'moment';

import { getLicensePlateOrCobliIdOrPlaceholder, type Vehicle } from '@cobliteam/common-types';

import { isVehicleDashcam, type UpcomingMaintenance } from '@cobliteam/api-lib';

import { CameraIdentifier } from '@cobliteam/media';

import { DescriptionListDefault, DescriptionTitle, PlaceholderText } from '@cobliteam/base-components';

import Icon from '@cobliteam/ui/icons';

export interface VehicleRemindersProps {
  vehicle: Vehicle;
  upcomingMaintenances: UpcomingMaintenance[];
  addTitles: boolean;
  onMarkAsDoneClick: () => void;
}

export const VehicleReminders: React.FC<VehicleRemindersProps> = ({ vehicle, upcomingMaintenances, addTitles, onMarkAsDoneClick }) => {
  const numberFormatter = new Intl.NumberFormat('pt-BR');

  const renderHeader = () => (
    <div className={styles.header}>
      <div className={styles.headerTitles}>
        <DescriptionListDefault />
        <DescriptionListDefault>Veículo</DescriptionListDefault>
        <DescriptionListDefault>Última manutenção</DescriptionListDefault>
        <DescriptionListDefault>Recorrência</DescriptionListDefault>
        <DescriptionListDefault>Próxima manutenção</DescriptionListDefault>
      </div>
      <div className={styles.right} />
    </div>
  );

  const renderRevisionTitle = (title?: string) => (
    <div className={styles.revisionName}>{title ? <DescriptionListDefault>{title}</DescriptionListDefault> : <PlaceholderText>Nenhuma descrição</PlaceholderText>}</div>
  );

  const renderReminderRow = (upM: UpcomingMaintenance, index: number) => {
    const isFirstRow = index === 0;
    const lastRevision = upM.lastRevisionDateInMillis && isFirstRow ? moment(upM.lastRevisionDateInMillis, 'x').format('DD/MM/YYYY') : '';

    const periodicityInKm = numberFormatter.format(Math.round(upM.revisionPeriodicityInMeters / 1000));

    const currentProgress = upM.metersDrivenSinceLastRevision / upM.revisionPeriodicityInMeters;

    const kmToRevision = Math.round((upM.revisionPeriodicityInMeters - upM.metersDrivenSinceLastRevision) / 1000);
    const formattedKmToRevision = numberFormatter.format(Math.abs(kmToRevision));

    return (
      <div id='row' className={styles.reminderRow} key={`${vehicle.id}-${index}`}>
        <div className={styles.iconColumn}>{isVehicleDashcam(vehicle) && <CameraIdentifier hasCta={false} />}</div>
        <div className={styles.vehicleColumn}>
          {isFirstRow && (
            <>
              <DescriptionTitle className={styles.licensePlate} ellipsis>
                {getLicensePlateOrCobliIdOrPlaceholder(vehicle)}
              </DescriptionTitle>
              <DescriptionListDefault ellipsis>
                {`${vehicle.model.brand || ''} ${vehicle.model.name || ''}`.trim() || <PlaceholderText>Marca Modelo</PlaceholderText>}
              </DescriptionListDefault>
            </>
          )}
        </div>
        <div className={styles.column}>
          <DescriptionListDefault>{lastRevision}</DescriptionListDefault>
        </div>
        <div className={styles.column}>
          <DescriptionListDefault>{`${periodicityInKm} km`}</DescriptionListDefault>
        </div>
        <div className={styles.nextRevisionColumn}>
          {renderRevisionTitle(upM.revisionName)}
          <div className={styles.progress}>
            <span
              id={upM.type}
              style={{
                width: `${Math.min(currentProgress, 1) * 100}%`,
              }}
            />
          </div>
          <div className={styles.nextMaintenance} id={upM.type}>
            {upM.type === 'delayed' && <Icon name='triangle-alert' />}
            {upM.type === 'delayed' ? `Atrasada há ${formattedKmToRevision} km` : `Daqui ${formattedKmToRevision} km`}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {addTitles && renderHeader()}
      <div className={styles.wrapper} id={upcomingMaintenances[0].type}>
        <div className={styles.leftContainer}>{upcomingMaintenances.map(renderReminderRow)}</div>
        <div className={styles.markAsDoneWrapper}>
          <Button color='success' size='condensed' startIcon={<Icon name='circle-check' />} onClick={onMarkAsDoneClick}>
            Marcar como feito
          </Button>
        </div>
      </div>
    </>
  );
};
